import React, { useEffect, useState } from 'react'
import { DateTime, Duration, DurationUnit, DurationUnits } from 'luxon'
import { Else, If, Then, When } from 'react-if'
import { AsLabel, CaptionMD } from '@/atoms/Text'
import { WatchingNowMessage } from '@/molecules/WatchingNowMessage'
import { useLivestream } from '@/organisms/Livestream'
import { useWatchPartyChat } from '@/services/WatchPartyChatService'
import { useLocale } from '@/utils/LocaleUtil'
import { useTranslate } from '@/utils/translate/translate-client'

const timeUnits = ['years', 'months', 'weeks', 'days', 'hours', 'minutes', 'seconds'] as DurationUnit[]

interface LivestreamViewershipProps {
  startAt: string
  live?: boolean
}

export const LivestreamViewership: React.FC<LivestreamViewershipProps> = ({ startAt }) => {
  const { locale } = useLocale()
  const { livestream } = useLivestream()
  const { t } = useTranslate('livestream')
  const { totalViewers } = useWatchPartyChat()

  const [elapsedStartAt, setElapsedStartAt] = useState<Record<DurationUnit, number>>({} as Record<DurationUnit, number>)

  useEffect(() => {
    const updateElapsedStartAt = () => {
      const diff = DateTime.fromISO(startAt, { locale })
        .diffNow(timeUnits as DurationUnits, { conversionAccuracy: 'casual' })
        .negate()
        .toObject() as Record<DurationUnit, number>

      setElapsedStartAt(diff)
    }

    updateElapsedStartAt()

    const interval = setInterval(() => {
      updateElapsedStartAt()
    }, 60000)

    return () => clearInterval(interval)
  }, [locale, startAt])

  let unitHadAValue = false

  const dateFormatter = Intl.DateTimeFormat(locale, { dateStyle: 'long', timeStyle: 'short' })

  timeUnits.forEach((unit) => {
    if (unitHadAValue) {
      delete elapsedStartAt?.[unit]
    } else if (elapsedStartAt?.[unit] || 0 > 0) {
      unitHadAValue = true
    } else {
      delete elapsedStartAt?.[unit]
    }
  })

  const startedDiff = Duration.fromObject(elapsedStartAt)
  const diffDisplay = startedDiff.toHuman({ listStyle: 'short', maximumFractionDigits: 0 })

  return (
    <CaptionMD weight="bold" as={AsLabel} color="core-gray-600" className="mb-4 flex flex-row items-center gap-2">
      {Boolean(!livestream?.isEnded && totalViewers) && (
        <>
          <WatchingNowMessage totalViewers={totalViewers} />
          <span>{'•'}</span>
        </>
      )}
      <If condition={livestream?.live}>
        <Then>
          <If condition={startedDiff.toMillis() > 0}>
            <Then>
              <span>
                {t('beganStreaming', 'Began streaming {{diffDisplay}} ago', {
                  diffDisplay,
                })}
              </span>
            </Then>
            <Else>
              <span>
                {t('streamBeginsIn', 'Stream begins in {{diffDisplay}}', {
                  diffDisplay: startedDiff.negate().toHuman({ listStyle: 'short', maximumFractionDigits: 0 }),
                })}
              </span>
            </Else>
          </If>
        </Then>
        <Else>
          {Boolean(!livestream?.isEnded && livestream?.startAt) && (
            <span>
              {t('streaming', 'Streaming on {{dateDisplay}}', {
                dateDisplay: dateFormatter.format(DateTime.fromISO(livestream?.startAt as string).toJSDate()),
              })}
            </span>
          )}
          <When condition={livestream?.isEnded}>
            <span>
              {t('streamed', 'Streamed {{diffDisplay}} ago', {
                diffDisplay,
              })}
            </span>
          </When>
        </Else>
      </If>
    </CaptionMD>
  )
}
